import { useEffect, useRef, useState } from "react";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";

/**
 * An interactive shell on the device, over the console channel.
 *
 * A real terminal emulator rather than a text box: the shell emits ANSI escape
 * sequences for colour, cursor movement and bracketed paste, and anything less
 * shows the operator garbage. Bytes travel base64-encoded in both directions —
 * the subprotocol is text-only and a shell emits arbitrary bytes.
 *
 * This component only draws and forwards keystrokes. Whether a shell may exist
 * at all is decided on the device, against state it fetched itself; a `denied`
 * envelope is the answer to that question, not an error here.
 */
export default function DeviceTerminal({ channel, group, sessionId, onClosed }) {
  const hostRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const [status, setStatus] = useState("opening");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!hostRef.current || !channel || !group) return undefined;

    const term = new Terminal({
      convertEol: false,
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
      fontSize: 13,
      scrollback: 5000,
      theme: { background: "#0f172a", foreground: "#e2e8f0", cursor: "#38bdf8" },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(hostRef.current);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;

    const send = (t, d) => channel.publish(group, {
      v: 1, ch: "pty", t, ts: Date.now() / 1000, sid: sessionId, d: d || {},
    });

    send("open", { cols: term.cols, rows: term.rows });
    term.writeln("\x1b[90mOpening the session…\x1b[0m");

    const onData = term.onData((data) => {
      // TextEncoder first: a multi-byte character must not be split across
      // frames, and btoa cannot take non-Latin-1 directly.
      const bytes = new TextEncoder().encode(data);
      let bin = "";
      bytes.forEach((b) => { bin += String.fromCharCode(b); });
      send("data", { b: btoa(bin) });
    });

    // Resize is a real signal, not cosmetics: without it `less`, `htop` and
    // line wrapping all use the wrong width.
    const onResize = () => {
      try {
        fit.fit();
        send("resize", { cols: term.cols, rows: term.rows });
      } catch {
        /* the element may be detached mid-teardown */
      }
    };
    window.addEventListener("resize", onResize);

    const handler = (env) => {
      if (env.ch !== "pty" || (env.sid && env.sid !== sessionId)) return;
      if (env.t === "opened") {
        setStatus("open");
        setDetail(env.d);
        term.clear();
        term.focus();
        send("resize", { cols: term.cols, rows: term.rows });
      } else if (env.t === "denied") {
        setStatus("denied");
        setDetail(env.d);
        term.writeln(`\x1b[31mThe device refused the request: ${env.d?.reason}\x1b[0m`);
      } else if (env.t === "data") {
        const raw = atob(env.d?.b || "");
        const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
        term.write(bytes);
      } else if (env.t === "dropped") {
        term.writeln(`\r\n\x1b[33m[dropped ${env.d?.bytes} bytes - the channel could not keep up]\x1b[0m`);
      } else if (env.t === "closed") {
        setStatus("closed");
        setDetail(env.d);
        term.writeln(`\r\n\x1b[90mSession closed: ${env.d?.reason}\x1b[0m`);
        if (onClosed) onClosed(env.d);
      }
    };
    channel.addPtyHandler(handler);

    return () => {
      window.removeEventListener("resize", onResize);
      channel.removePtyHandler(handler);
      onData.dispose();
      try {
        send("close", {});
      } catch {
        /* channel already gone */
      }
      term.dispose();
      termRef.current = null;
    };
  }, [channel, group, sessionId, onClosed]);

  const label = {
    opening: "opening…",
    open: "active",
    denied: "refused by the device",
    closed: "closed",
  }[status];

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className={status === "open" ? "text-emerald-700" : "text-slate-500"}>
          Session: {label}
        </span>
        {status === "open" && (
          <span className={detail?.recorded ? "text-slate-500" : "text-rose-700"}>
            {detail?.recorded
              ? "· recorded on the device"
              : "· NOT RECORDED (transcript directory unavailable)"}
          </span>
        )}
      </div>
      <div ref={hostRef} className="h-96 overflow-hidden rounded bg-slate-900 p-2" />
    </div>
  );
}
